import React, { useContext, useEffect, useState } from "react";
import { Box, GridLegacy as Grid } from "@mui/material";
import { SessionContext } from "@/utility/useSession";
import { useTranslation } from "react-i18next";
import { getFileUrl } from "@/utility/utils";
import flex from "@/styles/flex";
import fonts from "@/styles/fonts";
import styles from "../editable/EditableField/styles";
import { api, type Brand } from "@/logic/api";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";

interface BookingBrandLogoProps {
  agentCode?: string;
}

export const BookingBrandLogo: React.FC<BookingBrandLogoProps> = ({ agentCode }) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [loading, setLoading] = useState(false);
  const getBookingBrandHook = api.hooks.booking.getBookingBrand();
  const isImproveBookingBrandLogoReleased = useReleaseManagement(ReleaseFlag.releaseImproveBookingBrandLogo);

  useEffect(() => {
    if (agentCode) {
      setLoading(true);
      getBookingBrandHook.send(
        session.current.context,
        { brandCode: agentCode },
        (result) => {
          if (result) {
            setBrand(result);
          }
          setLoading(false);
        }
      );
    }
  }, [agentCode, session]);

  if (!session.current.site?.preferences.brand_enabled || !agentCode || loading) {
    return null;
  }

  if (!brand) {
    return null;
  }
  const logoUrl = brand.fileName ? getFileUrl(session, brand.fileName) : undefined;

  if (logoUrl) {
    return (
      <Grid item xs={12} sm={6}>
        <div style={flex.flexColumns} data-testid="booking-brand-logo">
          {isImproveBookingBrandLogoReleased && (
            <span style={fonts.small}>
              <div style={styles.paddingLabel}>{t("booking.labels.brand")}</div>
            </span>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'fit-content',
              ml: '10px',
              overflow: 'hidden',
                  ...(isImproveBookingBrandLogoReleased && { border: '1px solid rgba(0, 0, 0, 0.1)' }),
                  borderRadius: 1,
                  padding: "3px"
                // bgcolor: '#f8f9fa',
              }}
            >
              <Box
                component="img"
                src={logoUrl}
                alt={`${brand.displayName} logo`}
                title={`${brand.displayName}`}
                sx={{
                  display: 'block',
                  maxWidth: '120px',
                  maxHeight: '48px',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  // if for some reason the image fails to load, hide it and show display name instead
                  (e.target as HTMLImageElement).style.display = 'none';
                  const parent = (e.target as HTMLImageElement).parentElement;
                  if (parent) {
                    const textNode = document.createElement('span');
                    textNode.textContent = brand.displayName;
                    textNode.style.fontWeight = '500';
                    parent.appendChild(textNode);
                  }
                }}
              />
            </Box>
          </div>
        </Grid>
      );
    }

  return (
    <Grid item xs={12} sm={6}>
      <div style={flex.flexColumns} data-testid="booking-brand-logo">
        <div style={flex.flexFill}>
          <span style={fonts.small}>
            <div style={styles.paddingLabel}>{t("menuItems.brand")}</div>
          </span>
        </div>
        <div style={{ ...flex.flexFill, ...styles.text }}>
          <div style={styles.paddingViewable}>
            {brand.displayName}
          </div>
        </div>
      </div>
    </Grid>
  );
};
